/**
 * DS Coder — resolves the effective knowledge packs for a chat by merging the
 * DB-managed packs (global / workspace / user) over the built-in code defaults.
 *
 * Precedence (later wins): built-in defaults < global < workspace < user.
 * A DB row with is_enabled = false removes that tech's pack for the owner's
 * scope and below. Best-effort + cached: any failure falls back to the static
 * built-in packs so a chat is never blocked.
 */
import { listResolvablePacks } from "@/services/dsCoderPacksService";
import { KNOWLEDGE_PACKS } from "./knowledgePacks";
import type { DsCoderPack } from "@/types/dsCoderPacks";
import type { KnowledgePack, TechId } from "./types";

export type ResolvedPacks = Record<string, KnowledgePack>;

const CACHE_TTL_MS = 5 * 60 * 1000;
type CacheEntry = { value: ResolvedPacks; at: number };
const cache = new Map<string, CacheEntry>();

export function clearDsCoderPacksCache(): void {
  cache.clear();
}

function cacheKey(userId?: string | null, workspaceId?: string | null): string {
  return `${userId ?? "anon"}|${workspaceId ?? "none"}`;
}

function toKnowledgePack(pack: DsCoderPack): KnowledgePack {
  return {
    id: pack.techId as TechId,
    label: pack.label,
    docsLibraryId: pack.docsLibraryId ?? undefined,
    standards: pack.standards ?? [],
    commonErrors: pack.commonErrors ?? [],
    goldenLibraries: pack.goldenLibraries ?? [],
  };
}

/** Order DB packs so higher-precedence scopes are applied last. */
const SCOPE_ORDER: Record<DsCoderPack["scope"], number> = { global: 0, workspace: 1, user: 2 };

/**
 * Returns the effective packs map keyed by techId. Never throws.
 */
export async function resolveKnowledgePacks(opts: {
  userId?: string | null;
  workspaceId?: string | null;
}): Promise<ResolvedPacks> {
  const key = cacheKey(opts.userId, opts.workspaceId);
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.value;

  // Start from the built-in defaults.
  const merged: ResolvedPacks = { ...KNOWLEDGE_PACKS };

  try {
    const dbPacks = await listResolvablePacks(opts.workspaceId ?? null);
    const ordered = [...dbPacks].sort((a, b) => SCOPE_ORDER[a.scope] - SCOPE_ORDER[b.scope]);
    for (const pack of ordered) {
      if (pack.isEnabled) {
        merged[pack.techId] = toKnowledgePack(pack);
      } else {
        delete merged[pack.techId];
      }
    }
    cache.set(key, { value: merged, at: Date.now() });
    return merged;
  } catch (e) {
    console.warn("[DS Coder] Failed to resolve managed packs; using built-in defaults:", e);
    return merged;
  }
}
